import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/16/solid';
import { Button as DesignSystemButton } from '@leadsales/design-system/react';
import { useInterval, useTimeout } from 'ahooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Progress, Alert as UIAlert } from 'react-daisyui';
import { eAlertType } from '../Toast.enum';
import { tAlertProps } from '../Toast.types';

const DEFAULT_DURATION = 5000;
const TICK_MS = 100;

export default function Alert(props: tAlertProps) {
  const { alert, onDelete } = props;
  const { t } = useTranslation('common/toast');
  const duration = alert.duration ?? DEFAULT_DURATION;
  const steps = duration / TICK_MS;
  const [progress, setProgress] = useState(100);
  const [interval, setInterval] = useState<number | undefined>(TICK_MS);

  useInterval(() => {
    setProgress((prev) => {
      const next = prev - (100 / steps);
      if (next <= 0) {
        setInterval(undefined);
        return 0;
      }
      return next;
    });
  }, interval);

  useTimeout(() => onDelete(alert), duration);

  let icon = <InformationCircleIcon className="w-6 h-6 text-info" />;

  let color: 'success' | 'info' | 'warning' | 'error' = 'info';

  switch (alert.type) {
    case eAlertType.SUCCESS:
      icon = <CheckCircleIcon className="w-6 h-6 text-success" />;
      color = 'success';
      break;
    case eAlertType.INFO:
      icon = <InformationCircleIcon className="w-6 h-6 text-info" />;
      color = 'info';
      break;
    case eAlertType.WARNING:
      icon = <ExclamationTriangleIcon className="w-6 h-6 text-warning" />;
      color = 'warning';
      break;
    case eAlertType.ERROR:
      icon = <XCircleIcon className="w-6 h-6 text-error" />;
      color = 'error';
      break;
    default:
      break;
  }

  // Get the title: use provided title, or default translated title based on type
  const getTitle = () => {
    if (alert.title) return alert.title;
    // Default titles based on type
    switch (alert.type) {
      case eAlertType.SUCCESS:
        return t('successTitle');
      case eAlertType.INFO:
        return t('infoTitle');
      case eAlertType.WARNING:
        return t('warningTitle');
      case eAlertType.ERROR:
        return t('errorTitle');
      case eAlertType.SUMMARY:
        return t('summaryTitle');
      default:
        return alert.type;
    }
  };

  // A leaving toast plays its exit (data-exiting, daisyui.motion.scss) and is
  // inert from its first frame: out of the accessibility tree, the tab order
  // and the pointer. React 18 has no `inert` prop, so it goes as an attribute;
  // React 19 takes it as a boolean (`inert: true`).
  const exitProps = alert.exiting ? { 'data-exiting': '', inert: '' } : {};

  return (
    <UIAlert icon={icon} className="items-start" {...exitProps}>
      <div className="w-full">
        {!alert.hideTitle && <h3 className="font-bold">{getTitle()}</h3>}
        <p className="text-sm max-w-[400px] whitespace-break-spaces">{alert.message}</p>

        {alert.action && (
          <DesignSystemButton
            className="mt-2"
            color="primary"
            size="xs"
            type="button"
            onClick={() => {
              alert.action?.onClick();
              onDelete(alert);
            }}
          >
            {alert.action.label}
          </DesignSystemButton>
        )}

        <Progress
          color={color}
          max="100"
          value={progress}
          className="mt-4 w-56"
        />
      </div>

      <Button size="sm" shape="circle" onClick={() => onDelete(alert)}>
        x
      </Button>
    </UIAlert>
  );
}
